/**
 * Episode Generation API Client
 *
 * React Query hooks for episode generation endpoints:
 * - POST /api/admin/generation/generate (synchronous)
 * - POST /api/admin/generation/refine (synchronous)
 *
 * Story: 11.5 - Basic Episode Generator
 * Acceptance Criteria: AC 3 - Task 11
 */

import { useMutation } from '@tanstack/react-query';
import { apiPost } from 'services/api';

// ============================================================================
// Types
// ============================================================================

/**
 * Persona Schema
 * Matches backend PersonaSchema
 * Story 3.11: thumbnailUrl added after image generation (prompts are now templates)
 */
export interface Persona {
  name: string;
  role: string;
  description: string;
  thumbnailUrl?: string | null; // Story 3.11: URL after image generation
}

/**
 * Dictionary Entry Schema
 * Story 3.11: Contextual vocabulary for episode
 */
export interface DictionaryEntry {
  word: string;
  explanation: string;
  examplePhrases: string[];
}

/**
 * Generated Episode Structure
 * Matches backend EpisodeGenerationSchema output
 * Story 3.9.15: description removed (episodes are now language-specific with context)
 * Story 3.11: Added visual assets URLs (prompts are now templates) and dictionary
 * Story 15.8: tagIds are now integers (migrated from UUID)
 */
export interface GeneratedEpisode {
  name: string;
  context: string;
  goals: string[];
  personas: Persona[];
  scenario: string;
  durationId?: number; // Story 15.11: Duration ID (1=Micro, 2=Short, 3=Medium, 4=Long, 5=Extended)
  complexityLevel?: number;
  tagIds?: number[]; // Story 15.8: Integer IDs
  // Story 3.11: Visual assets URLs (generated from templates)
  thumbnailUrl?: string | null;
  backgroundImageUrl?: string | null;
  // Story 3.11: Contextual vocabulary
  dictionary?: DictionaryEntry[] | null;
}

/**
 * Episode Generation Request
 * Maps to backend GenerateRequest schema (snake_case API fields)
 * Story 15.8: tags are now integer IDs (migrated from UUID)
 */
export interface GenerateEpisodeRequest {
  idea: string; // max 500 chars
  language?: string; // ISO 639-1 code (en, es, fr, de), default: "en"
  complexity?: number; // 1-5
  tags?: number[]; // Story 15.8: Integer tag IDs
  durationId?: number; // Story 15.11: Duration ID (1=Micro, 2=Short, 3=Medium, 4=Long, 5=Extended)
  titleOverride?: string; // Optional title to override LLM-generated episode name
  signal?: AbortSignal; // Optional abort signal for cancellation
}

/**
 * Episode Refinement Request
 * Maps to backend RefineRequest schema (snake_case API fields)
 */
export interface RefineEpisodeRequest {
  episode: GeneratedEpisode; // Current episode structure
  instructions: string; // max 500 chars
}

/**
 * Generation Metadata
 * Tracks generation process details
 */
export interface GenerationMetadata {
  iterationsTaken: number; // 1-5
  finalCritiqueDecision: 'ACCEPT' | 'NEEDS_IMPROVEMENT';
  critiqueFeedback: string[];
}

/**
 * Generate Episode Response
 * Maps to backend GenerateResponse (snake_case API, camelCase client)
 */
export interface GenerateEpisodeResponse {
  episode: GeneratedEpisode;
  generationMetadata: GenerationMetadata;
  episodeId: string; // Story 3.11: Pre-generated UUID for image assets, use when creating episode
}

/**
 * Refine Episode Response
 * Maps to backend RefineResponse (snake_case API, camelCase client)
 */
export interface RefineEpisodeResponse {
  episode: GeneratedEpisode;
  changesMade: string[];
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Generate Episode
 * Calls POST /api/admin/generation/generate
 * Synchronous endpoint (2-5 minutes, 300s timeout)
 */
async function generateEpisode(request: GenerateEpisodeRequest): Promise<GenerateEpisodeResponse> {
  // Convert camelCase to snake_case for API
  // Story 15.11: duration changed to duration_id
  const apiRequest = {
    idea: request.idea,
    language: request.language || 'en',
    complexity: request.complexity,
    tags: request.tags,
    duration_id: request.durationId,
    title_override: request.titleOverride || undefined,
  };

  // Story 15.11: duration_id is now an integer (1-4)
  // Story 3.9.15: description removed from episode schema
  // Story 3.11: Visual assets URLs (prompts are templates now)
  const response = await apiPost<{
    episode: {
      name: string;
      context: string;
      goals: string[];
      personas: {
        name: string;
        role: string;
        description: string;
        thumbnail_url?: string | null;
      }[];
      scenario: string;
      duration_id?: number;
      complexity_level?: number;
      tag_ids?: number[]; // Story 15.8: Integer IDs
      // Story 3.11: Visual assets URLs
      thumbnail_url?: string | null;
      background_image_url?: string | null;
      // Story 3.11: Dictionary
      dictionary?: { word: string; explanation: string; example_phrases: string[] }[] | null;
    };
    generation_metadata: {
      iterations_taken: number;
      final_critique_decision: 'ACCEPT' | 'NEEDS_IMPROVEMENT';
      critique_feedback: string[];
    };
    episode_id: string; // Story 3.11: Pre-generated UUID for image assets
  }>('/api/admin/generation/generate', apiRequest, 300000, request.signal); // 5 minutes

  // Convert snake_case to camelCase for client
  // Story 3.9.15: description removed
  // Story 3.11: Visual assets URLs (prompts are templates now)
  return {
    episode: {
      name: response.episode.name,
      context: response.episode.context,
      goals: response.episode.goals,
      personas: response.episode.personas.map((p) => ({
        name: p.name,
        role: p.role,
        description: p.description,
        thumbnailUrl: p.thumbnail_url,
      })),
      scenario: response.episode.scenario,
      durationId: response.episode.duration_id,
      complexityLevel: response.episode.complexity_level,
      tagIds: response.episode.tag_ids,
      // Story 3.11: Visual assets URLs
      thumbnailUrl: response.episode.thumbnail_url,
      backgroundImageUrl: response.episode.background_image_url,
      // Story 3.11: Dictionary
      dictionary: response.episode.dictionary?.map((d) => ({
        word: d.word,
        explanation: d.explanation,
        examplePhrases: d.example_phrases,
      })),
    },
    generationMetadata: {
      iterationsTaken: response.generation_metadata.iterations_taken,
      finalCritiqueDecision: response.generation_metadata.final_critique_decision,
      critiqueFeedback: response.generation_metadata.critique_feedback,
    },
    episodeId: response.episode_id, // Story 3.11: Pre-generated UUID for image assets
  };
}

/**
 * Refine Episode
 * Calls POST /api/admin/generation/refine
 * Synchronous endpoint (can take up to 3 minutes for complex refinements)
 */
async function refineEpisode(request: RefineEpisodeRequest): Promise<RefineEpisodeResponse> {
  // Convert camelCase to snake_case for API
  // Story 15.11: duration_id is now an integer
  // Story 3.9.15: description removed
  // Story 3.11: Visual assets URLs (prompts are templates now)
  const apiRequest = {
    episode: {
      name: request.episode.name,
      context: request.episode.context,
      goals: request.episode.goals,
      personas: request.episode.personas.map((p) => ({
        name: p.name,
        role: p.role,
        description: p.description,
        thumbnail_url: p.thumbnailUrl,
      })),
      scenario: request.episode.scenario,
      duration_id: request.episode.durationId,
      complexity_level: request.episode.complexityLevel,
      tag_ids: request.episode.tagIds,
      // Story 3.11: Visual assets URLs
      thumbnail_url: request.episode.thumbnailUrl,
      background_image_url: request.episode.backgroundImageUrl,
      // Story 3.11: Dictionary
      dictionary: request.episode.dictionary?.map((d) => ({
        word: d.word,
        explanation: d.explanation,
        example_phrases: d.examplePhrases,
      })),
    },
    instructions: request.instructions,
  };

  const response = await apiPost<{
    episode: {
      name: string;
      context: string;
      goals: string[];
      personas: {
        name: string;
        role: string;
        description: string;
        thumbnail_url?: string | null;
      }[];
      scenario: string;
      duration_id?: number;
      complexity_level?: number;
      tag_ids?: number[]; // Story 15.8: Integer IDs
      // Story 3.11: Visual assets URLs
      thumbnail_url?: string | null;
      background_image_url?: string | null;
      // Story 3.11: Dictionary
      dictionary?: { word: string; explanation: string; example_phrases: string[] }[] | null;
    };
    changes_made: string[];
  }>('/api/admin/generation/refine', apiRequest, 180000); // 3 minutes

  // Convert snake_case to camelCase for client
  // Story 3.9.15: description removed
  // Story 3.11: Visual assets URLs (prompts are templates now)
  return {
    episode: {
      name: response.episode.name,
      context: response.episode.context,
      goals: response.episode.goals,
      personas: response.episode.personas.map((p) => ({
        name: p.name,
        role: p.role,
        description: p.description,
        thumbnailUrl: p.thumbnail_url,
      })),
      scenario: response.episode.scenario,
      durationId: response.episode.duration_id,
      complexityLevel: response.episode.complexity_level,
      tagIds: response.episode.tag_ids,
      // Story 3.11: Visual assets URLs
      thumbnailUrl: response.episode.thumbnail_url,
      backgroundImageUrl: response.episode.background_image_url,
      // Story 3.11: Dictionary
      dictionary: response.episode.dictionary?.map((d) => ({
        word: d.word,
        explanation: d.explanation,
        examplePhrases: d.example_phrases,
      })),
    },
    changesMade: response.changes_made,
  };
}

// ============================================================================
// React Query Hooks
// ============================================================================

/**
 * Generate Episode Mutation Hook
 * Usage:
 *   const { mutate, isPending, error } = useGenerateEpisode();
 *   mutate({ idea: '...', complexity: 3 });
 */
export function useGenerateEpisode() {
  return useMutation({
    mutationFn: generateEpisode,
    // No automatic cache invalidation (episode not saved yet)
  });
}

/**
 * Refine Episode Mutation Hook
 * Usage:
 *   const { mutate, isPending, error } = useRefineEpisode();
 *   mutate({ episode: {...}, instructions: '...' });
 */
export function useRefineEpisode() {
  return useMutation({
    mutationFn: refineEpisode,
    // No automatic cache invalidation (episode not saved yet)
  });
}
